import React, { useEffect, useRef, useState } from 'react';
import { FiAlertCircle, FiLoader } from 'react-icons/fi';

export function SwaggerUiViewer({ spec, specJson }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const activeSpec = specJson || spec;

  useEffect(() => {
    let isMounted = true;

    const loadSwaggerUI = async () => {
      try {
        setLoading(true);
        setError(null);

        // Inject Swagger UI stylesheet if not present
        if (!document.getElementById('swagger-ui-css')) {
          const link = document.createElement('link');
          link.id = 'swagger-ui-css';
          link.rel = 'stylesheet';
          link.href = 'https://cdnjs.cloudflare.com/ajax/libs/swagger-ui/5.11.0/swagger-ui.min.css';
          document.head.appendChild(link);
        }

        // Inject Swagger UI bundle if not present
        if (!window.SwaggerUIBundle) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/swagger-ui/5.11.0/swagger-ui-bundle.min.js';
            script.crossOrigin = 'anonymous';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Swagger UI bundle script.'));
            document.body.appendChild(script);
          });
        }

        if (!isMounted || !containerRef.current) return;

        const parsedSpec = typeof activeSpec === 'string' ? JSON.parse(activeSpec) : activeSpec;
        containerRef.current.innerHTML = '';

        window.SwaggerUIBundle({
          spec: parsedSpec,
          domNode: containerRef.current,
          deepLinking: true,
          presets: [window.SwaggerUIBundle.presets.apis],
          layout: 'BaseLayout',
          docExpansion: 'list',
          defaultModelsExpandDepth: 1,
        });

        if (isMounted) {
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to render Swagger UI');
          setLoading(false);
        }
      }
    };

    loadSwaggerUI();

    return () => {
      isMounted = false;
    };
  }, [spec]);

  return (
    <div className="relative min-h-[420px] w-full rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/90 backdrop-blur-sm z-10 rounded-xl">
          <FiLoader className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-sm font-medium text-slate-700">Rendering interactive Swagger UI...</p>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 text-red-700 border border-red-200">
          <FiAlertCircle size={20} className="shrink-0" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      <div ref={containerRef} className="swagger-ui-container" />
    </div>
  );
}

export default SwaggerUiViewer;
